import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bell, Loader2, Trash2, ExternalLink } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ConfirmDialog,
} from 'design-system'
import {
  clearAllNotifications,
  fetchNotifications,
  fetchSummary,
  markAsRead,
  markAllAsRead,
} from 'slices/notificationsSlice'
import { NotificationListItem } from 'pages/Notifications/NotificationListItem'
import {
  TAB_ALL,
  TAB_UNREAD,
  TAB_ALERTS,
  tabToApiParam,
  type NotificationItem,
} from 'pages/Notifications/notificationUi'
import { useBranchAware } from 'hooks/useBranchAware'

const DROPDOWN_PER_PAGE = 25

const NotificationDropdown = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isGlobalView } = useBranchAware()
  const {
    data: notifications,
    unreadCount,
    summary,
    loading,
  } = useSelector(
    (state: any) =>
      state.notifications || {
        data: [],
        unreadCount: 0,
        summary: { total: 0, unread: 0, alerts: 0 },
        loading: false,
      }
  )

  const [isNotificationDropdown, setIsNotificationDropdown] = useState(false)
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)
  const [clearingAll, setClearingAll] = useState(false)
  const [activeTab, setActiveTab] = useState(TAB_ALL)

  const loadNotifications = (tab: string) => {
    dispatch(
      fetchNotifications({
        tab: tabToApiParam(tab),
        page: 1,
        per_page: DROPDOWN_PER_PAGE,
      }) as any
    )
  }

  const toggleNotificationDropdown = (open: boolean) => {
    setIsNotificationDropdown(open)
    if (open) {
      loadNotifications(activeTab)
      dispatch(fetchSummary() as any)
    }
  }

  const toggleTab = (tab: string) => {
    setActiveTab(tab)
    if (isNotificationDropdown) {
      loadNotifications(tab)
    }
  }

  useEffect(() => {
    dispatch(fetchSummary() as any)
    const interval = setInterval(() => {
      dispatch(fetchSummary() as any)
    }, 30000)
    return () => clearInterval(interval)
  }, [dispatch])

  const handleNotificationClick = (notification: NotificationItem) => {
    if (!notification.read) {
      dispatch(markAsRead(notification.id) as any)
    }
    if (notification.action_url) {
      setIsNotificationDropdown(false)
      navigate(notification.action_url)
    }
  }

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead() as any)
  }

  const handleClearAll = async () => {
    setClearingAll(true)
    try {
      await dispatch(clearAllNotifications() as any)
      setClearConfirmOpen(false)
    } finally {
      setClearingAll(false)
    }
  }

  const handleViewAll = () => {
    setIsNotificationDropdown(false)
    navigate('/notifications')
  }

  const renderNotificationList = (emptyMessage: string) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )
    }
    if (notifications.length === 0) {
      return (
        <div className="flex items-center justify-center py-8">
          <p className="mb-0 text-sm text-muted-foreground">{t(emptyMessage)}</p>
        </div>
      )
    }
    return (
      <>
        {notifications.map((notification: NotificationItem) => (
          <NotificationListItem
            key={notification.id}
            notification={notification}
            onClick={handleNotificationClick}
            t={t}
            showBranchName={isGlobalView}
          />
        ))}
      </>
    )
  }

  const emptyMessages: Record<string, string> = {
    [TAB_ALL]: 'No notifications',
    [TAB_UNREAD]: 'No unread notifications',
    [TAB_ALERTS]: 'No alerts',
  }

  return (
    <>
      <DropdownMenu open={isNotificationDropdown} onOpenChange={toggleNotificationDropdown}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="relative inline-flex h-8 w-8 items-center justify-center rounded-full outline-none transition-colors hover:bg-accent"
          >
            <Bell className="h-[22px] w-[22px]" />
            {unreadCount > 0 && (
              <span className="absolute -end-1 -top-1">
                <Badge
                  variant="destructive"
                  className="flex h-5 min-w-[20px] items-center justify-center px-1 text-[10px]"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          dir="rtl"
          className="flex w-80 flex-col overflow-hidden border-border-subtle bg-popover p-0 text-start text-popover-foreground shadow-lg"
        >
          <div className="shrink-0 border-b border-border-subtle p-3">
            <div className="flex items-center justify-between gap-2">
              <h6 className="m-0 text-base font-semibold text-foreground">{t('Notifications')}</h6>
              <div className="flex items-center gap-2">
                {summary.total > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="inline-flex h-7 items-center gap-1 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => setClearConfirmOpen(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5 shrink-0" />
                    {t('common.notifications.clearAll')}
                  </Button>
                )}
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {unreadCount} {t('New')}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={toggleTab} dir="rtl" className="flex min-h-0 flex-1 flex-col">
            <TabsList
              dir="rtl"
              className="mx-3 mb-0 mt-2 flex h-8 w-[calc(100%-1.5rem)] flex-row justify-start border border-border-subtle bg-muted"
            >
              <TabsTrigger value={TAB_ALL} className="flex-1 text-xs">
                {t('All')} ({summary.total})
              </TabsTrigger>
              <TabsTrigger value={TAB_UNREAD} className="flex-1 text-xs">
                {t('Unread')} ({summary.unread})
              </TabsTrigger>
              <TabsTrigger value={TAB_ALERTS} className="flex-1 text-xs">
                {t('Alerts')} ({summary.alerts})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={TAB_ALL} className="mt-0 min-h-0 flex-1 data-[state=inactive]:hidden">
              <div className="max-h-[min(360px,calc(100dvh-10rem))] overflow-y-auto overscroll-contain">
                {renderNotificationList(emptyMessages[TAB_ALL])}
              </div>
            </TabsContent>
            <TabsContent value={TAB_UNREAD} className="mt-0 min-h-0 flex-1 data-[state=inactive]:hidden">
              <div className="max-h-[min(360px,calc(100dvh-10rem))] overflow-y-auto overscroll-contain">
                {renderNotificationList(emptyMessages[TAB_UNREAD])}
              </div>
            </TabsContent>
            <TabsContent value={TAB_ALERTS} className="mt-0 min-h-0 flex-1 data-[state=inactive]:hidden">
              <div className="max-h-[min(360px,calc(100dvh-10rem))] overflow-y-auto overscroll-contain">
                {renderNotificationList(emptyMessages[TAB_ALERTS])}
              </div>
            </TabsContent>
          </Tabs>

          <div className="shrink-0 space-y-1 border-t border-border-subtle bg-muted/30 p-2" dir="rtl">
            {summary.unread > 0 && (
              <Button variant="link" size="sm" className="h-8 w-full text-center" onClick={handleMarkAllAsRead}>
                {t('Mark all as read')}
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-9 w-full gap-1.5" onClick={handleViewAll}>
              <ExternalLink className="h-4 w-4 shrink-0" />
              {t('View all notifications')}
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        open={clearConfirmOpen}
        onOpenChange={setClearConfirmOpen}
        title={t('common.notifications.confirmClearTitle')}
        description={t('common.notifications.confirmClearDescription')}
        confirmText={t('common.notifications.confirmClearConfirm')}
        cancelText={t('common.cancel')}
        variant="destructive"
        loading={clearingAll}
        onConfirm={handleClearAll}
      />
    </>
  )
}

export default NotificationDropdown
